import { ChatConversation } from '../../../../contexts/ChatContext';

interface ConversationListProps {
  conversations: ChatConversation[];
  selectedId: string | null;
  onSelect: (conv: ChatConversation) => void;
  search: string;
  onSearchChange: (v: string) => void;
}

function formatRelativeTime(iso: string) {
  const now = Date.now();
  const diff = now - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  search,
  onSearchChange,
}: ConversationListProps) {
  const filtered = conversations.filter(
    (c) =>
      c.userName.toLowerCase().includes(search.toLowerCase()) ||
      c.messages.some((m) => m.text.toLowerCase().includes(search.toLowerCase()))
  );

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-stone-100">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-stone-100 rounded-lg outline-none focus:bg-stone-200 transition"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <i className="ri-chat-off-line text-stone-300 text-2xl" />
            <p className="text-xs text-stone-400">No conversations found</p>
          </div>
        )}
        {sorted.map((conv) => {
          const unread = conv.messages.filter((m) => m.sender === 'user' && !m.read).length;
          const last = conv.messages[conv.messages.length - 1];
          const active = selectedId === conv.id;

          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className={`w-full text-left px-4 py-3.5 border-b border-stone-100 transition cursor-pointer ${
                active ? 'bg-orange-50' : 'hover:bg-stone-50'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-white text-sm font-bold">
                    {conv.userName.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <p className={`text-sm font-semibold truncate ${active ? 'text-orange-700' : 'text-stone-800'}`}>
                      {conv.userName}
                    </p>
                    <span className="text-[10px] text-stone-400 shrink-0">
                      {formatRelativeTime(conv.lastActivity)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-1 mt-0.5">
                    <p className="text-xs text-stone-500 truncate">
                      {last ? (
                        <>
                          {last.sender === 'admin' && (
                            <span className="text-stone-400">You: </span>
                          )}
                          {last.text}
                        </>
                      ) : (
                        'No messages yet'
                      )}
                    </p>
                    {unread > 0 && (
                      <span className="w-4 h-4 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">
                        {unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
